import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { ArticleJsonLd } from "@/components/JsonLd";
import { Button } from "@/components/ui/button";
import { resourceGuides } from "@/data/resourceGuides";

const ResourceGuide = ({ slug }: { slug: string }) => {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const lang = isDe ? "de" : "en";
  const guides = resourceGuides[lang];
  const guide = guides.find((item) => item.slug === slug);
  if (!guide) return null;
  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={`${guide.title} | rawResponse Labs`} description={guide.description} />
      <ArticleJsonLd title={guide.title} description={guide.description} slug={slug} language={lang} dateModified="2026-11-03" />
      <Navbar />
      <main className="mx-auto max-w-[840px] px-6 pb-24 pt-32 md:pt-40">
        <article>
          <header>
            <Link to="/resources" className="text-sm text-muted-foreground underline-offset-4 hover:underline">{isDe ? "Wissen & FAQ" : "Guides & FAQ"}</Link>
            <h1 className="mb-6 mt-8 text-[32px] font-medium leading-[1.15] tracking-normal md:text-[48px]">{guide.title}</h1>
            <p className="text-[18px] leading-relaxed text-muted-foreground">{guide.description}</p>
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <Link to="/about" className="underline-offset-4 hover:underline">Levent Valente · rawResponse Labs</Link>
              <span>{isDe ? "Aktualisiert" : "Updated"} <time dateTime="2026-11-03">{isDe ? "3. November 2026" : "3 November 2026"}</time></span>
            </div>
          </header>
          <section aria-labelledby="short-answer" className="mt-10 border-y border-border py-8">
            <h2 id="short-answer" className="mb-3 text-lg font-medium tracking-normal">{isDe ? "Kurzantwort" : "The short answer"}</h2>
            <p className="m-0 text-[17px] leading-[1.8]">{guide.summary}</p>
          </section>
          <nav aria-label={isDe ? "Inhaltsverzeichnis" : "Contents"} className="py-8">
            <ol className="space-y-3">
              {guide.sections.map((section, i) => <li key={section.id} className="flex gap-3 text-sm"><span className="text-muted-foreground">0{i + 1}</span><a href={`#${section.id}`} className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">{section.title}</a></li>)}
            </ol>
          </nav>
          {guide.sections.map((section) => (
            <section id={section.id} key={section.id} className="scroll-mt-28 border-t border-border py-10">
              <h2 className="mb-6 text-[24px] font-medium leading-snug tracking-normal md:text-[28px]">{section.title}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph} className="mb-5 text-[17px] leading-[1.85] text-muted-foreground">{paragraph}</p>)}
              {section.checklist && <ul className="ml-5 list-disc space-y-3 text-[16px] leading-relaxed text-muted-foreground">{section.checklist.map((item) => <li key={item}>{item}</li>)}</ul>}
            </section>
          ))}
          <aside className="border-t border-border py-10" aria-labelledby="related-heading">
            <h2 id="related-heading" className="mb-5 text-xl font-medium tracking-normal">{isDe ? "Weiterlesen & nächste Schritte" : "Further reading & next steps"}</h2>
            <div className="flex flex-col items-start gap-4 text-[16px]">
              {guides.filter((item) => item.slug !== slug).map((item) => <Link key={item.slug} to={`/resources/${item.slug}`} className="underline-offset-4 hover:underline">{item.title} ↗</Link>)}
              <Link to={slug === "agency-blueprint" ? "/solutions/agencies" : "/solutions/enterprises"} className="text-muted-foreground underline-offset-4 hover:underline">{isDe ? "Agentische Systeme für Ihr Team" : "Agentic systems for your team"} ↗</Link>
              <a href="https://webblitz.ai" target="_blank" rel="noopener noreferrer" className="text-muted-foreground underline-offset-4 hover:underline">webblitz.ai · {isDe ? "KI-Sichtbarkeit" : "AI visibility"} ↗</a>
            </div>
            <Button asChild variant="secondary" className="mt-8"><Link to="/contact">{isDe ? "Erstgespräch anfragen" : "Request an intro call"}<ArrowRight /></Link></Button>
          </aside>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default ResourceGuide;